import { handleActions, createAction } from "redux-actions";
import {
    createThreadAPI,
    deleteCommentAPI,
    getCommentsAPI,
    getThreadsAPI,
    updateCommentAPI,
    getCommentAPI,
    createActionsAPI,
    createCommentAPI,
    getThreadAPI,
    deleteThreadAPI,
    getThreadMembersAPI,
    updateThreadAPI,
    updateThreadMembersAPI,
    deleteCommentAttachmentAPI,
    addCommentAttachmentsAPI,
} from "@modules/apis/comments";
import { withAbortHandling } from "@hooks/useAbortController";

// Blob URL 메모리 해제를 위한 헬퍼 함수
const revokeBlobUrls = (comment) => {
    if (comment?.attachments) {
        comment.attachments.forEach((attachment) => {
            if (attachment?.url && attachment.url.startsWith("blob:")) {
                URL.revokeObjectURL(attachment.url);
            }
        });
    }
};

const initialState = {
    comment: {
        data: {
            results: null,
            prevToken: null,
            nextToken: null,
        },
        prevPending: null,
        nextPending: null,
        error: null,
        createPending: null,
        firstLoading: true,
    },
    resourceData: null, //Note: domain에 연결된 스레드
    resourcePending: null,
    selectedThreadId: null, //Note: 선택된 스레드 id(댓글 용)
    selectedCommentId: null, //Note: 선택된 댓글 id(대댓글 용)
    thread: {
        data: {
            results: null,
            prevToken: null,
            nextToken: null,
        },
        prevPending: null,
        nextPending: null,
    }, //Note: 댓글에 연결된 스레드(대댓글)
    isEditingCommentId: null,
    isLinkCommentUsed: false,
    hasLinkThreadLogicExecuted: false, //Note: 메일 링크로 대댓글창을 직접 접근하는 경우, 댓글창으로 나갈 경우 1회만 threads, comments를 불러줘야 함
    uploadingComments: {},
};

const cloneState = { ...initialState };

const GET_RESOURCE_DATA_PENDING = "GET_RESOURCE_DATA_PENDING";
const GET_RESOURCE_DATA_SUCCESS = "GET_RESOURCE_DATA_SUCCESS";
const GET_RESOURCE_DATA_FAILURE = "GET_RESOURCE_DATA_FAILURE";
const UPDATE_RESOURCE_DATA_SUCCESS = "UPDATE_RESOURCE_DATA_SUCCESS";
const UPDATE_RESOURCE_DATA_MEMBERS_SUCCESS = "UPDATE_RESOURCE_DATA_MEMBERS_SUCCESS";
const GET_COMMENTS_PENDING = "GET_COMMENTS_PENDING";
const GET_PREV_COMMENTS_SUCCESS = "GET_PREV_COMMENTS_SUCCESS";
const GET_NEXT_COMMENTS_SUCCESS = "GET_NEXT_COMMENTS_SUCCESS";
const GET_COMMENTS_FAILURE = "GET_COMMENTS_FAILURE";
const RESET_COMMENTS = "RESET_COMMENTS";
const UPDATE_SELECTED_COMMENT_ID = "UPDATE_SELECTED_COMMENT_ID";
const UPDATE_SELECTED_THREAD_ID = "UPDATE_SELECTED_THREAD_ID";
const RESET_SELECTED_COMMENT_ID = "RESET_SELECTED_COMMENT_ID";
const UPDATE_IS_EDITING_COMMENT_ID = "UPDATE_IS_EDITING_COMMENT_ID";
const GET_THREAD_COMMENTS_PENDING = "GET_THREAD_COMMENTS_PENDING";
const GET_THREAD_PREV_COMMENTS_SUCCESS = "GET_THREAD_PREV_COMMENTS_SUCCESS";
const GET_THREAD_NEXT_COMMENTS_SUCCESS = "GET_THREAD_NEXT_COMMENTS_SUCCESS";
const GET_THREAD_COMMENTS_FAILURE = "GET_THREAD_COMMENTS_FAILURE";
const RESET_THREAD_COMMENTS = "RESET_THREAD_COMMENTS";
const GET_PARENT_COMMENT_SUCCESS = "GET_PARENT_COMMENT_SUCCESS";
const UPDATE_COMMENT_SUCCESS = "UPDATE_COMMENT_SUCCESS";
const UPDATE_THREAD_SUCCESS = "UPDATE_THREAD_SUCCESS";
const ADD_THREAD_SUCCESS = "ADD_THREAD_SUCCESS";
const ADD_COMMENT_SUCCESS = "ADD_COMMENT_SUCCESS";
const ADD_THREAD_ALL_SUCCESS = "ADD_THREAD_ALL_SUCCESS";
const CREATE_THREAD_SUCCESS = "CREATE_THREAD_SUCCESS";
const CREATE_THREAD_PENDING = "CREATE_THREAD_PENDING";
const UPDATE_IS_LINK_COMMENT_USED = "UPDATE_IS_LINK_COMMENT_USED";
const UPDATE_HAS_LINK_THREAD_LOGIC_EXECUTED = "UPDATE_HAS_LINK_THREAD_LOGIC_EXECUTED";
const RESET_STATE = "RESET_STATE";
const ADD_UPLOADING_COMMENTS = "ADD_UPLOADING_COMMENTS";
const REMOVE_UPLOADING_COMMENTS = "REMOVE_UPLOADING_COMMENTS";
const UPDATE_UPLOADING_COMMENTS = "UPDATE_UPLOADING_COMMENTS";

export default handleActions(
    {
        [GET_RESOURCE_DATA_PENDING]: (state) => {
            return {
                ...state,
                resourceData: { ...cloneState.resourceData },
                resourcePending: true,
            };
        },
        [GET_RESOURCE_DATA_SUCCESS]: (state, action) => {
            return {
                ...state,
                resourcePending: null,
                resourceData: action.payload,
            };
        },
        [GET_RESOURCE_DATA_FAILURE]: (state, action) => {
            return { ...state, resourcePending: null, error: action.payload };
        },
        [UPDATE_RESOURCE_DATA_SUCCESS]: (state, action) => {
            const resourceDataResults = state.resourceData.results;
            const targetIndex = resourceDataResults.findIndex((el) => el.id === action.payload.id);
            resourceDataResults[targetIndex] = {
                ...action.payload,
                members: resourceDataResults[targetIndex].members,
            };
            return {
                ...state,
                resourceData: {
                    ...state.resourceData,
                    results: resourceDataResults,
                },
            };
        },
        [UPDATE_RESOURCE_DATA_MEMBERS_SUCCESS]: (state, action) => {
            const resourceDataResults = state.resourceData.results;
            const targetIndex = resourceDataResults.findIndex((el) => el.id === action.payload.id);
            resourceDataResults[targetIndex] = {
                ...resourceDataResults[targetIndex],
                members: action.payload.members,
            };
            return {
                ...state,
                resourceData: {
                    ...state.resourceData,
                    results: resourceDataResults,
                },
            };
        },
        [GET_COMMENTS_PENDING]: (state, action) => {
            const pending = action.payload === "ASC" ? "nextPending" : "prevPending";

            return { ...state, comment: { ...state.comment, [pending]: true } };
        },
        [GET_NEXT_COMMENTS_SUCCESS]: (state, action) => {
            const { cursor, data } = action.payload;
            const results = state.comment?.data?.results?.filter((el) => el.id !== cursor) ?? [];
            let newDataResults = results ? [...data.results.reverse(), ...results] : [...data.results.reverse()];

            return {
                ...state,
                comment: {
                    ...state.comment,
                    nextPending: null,
                    data: {
                        ...state.comment.data,
                        results: newDataResults,
                        nextToken: action.payload.data.nextToken,
                    },
                },
            };
        },
        [GET_PREV_COMMENTS_SUCCESS]: (state, action) => {
            const data = state.comment?.data?.results ?? [];

            const existingIds = new Set(data?.map((item) => item.id) ?? []);

            const uniqueNewResults = action.payload.data.results.filter((item) => !existingIds.has(item.id));

            const newDataResults = data ? [...data, ...uniqueNewResults] : [...uniqueNewResults];

            return {
                ...state,
                comment: {
                    ...state.comment,
                    prevPending: null,
                    data: {
                        ...state.comment.data,
                        results: newDataResults,
                        prevToken: action.payload.data.nextToken,
                    },
                    firstLoading: false,
                },
            };
        },
        [GET_COMMENTS_FAILURE]: (state, action) => {
            return {
                ...state,
                comment: {
                    ...state.comment,
                    prevPending: false,
                    error: action.payload,
                },
            };
        },
        [RESET_COMMENTS]: (state) => {
            return { ...state, comment: { ...cloneState.comment } };
        },
        [UPDATE_SELECTED_THREAD_ID]: (state, action) => {
            return { ...state, selectedThreadId: action.payload };
        },
        [UPDATE_SELECTED_COMMENT_ID]: (state, action) => {
            return { ...state, selectedCommentId: action.payload };
        },
        [RESET_SELECTED_COMMENT_ID]: (state) => {
            return {
                ...state,
                selectedCommentId: null,
                thread: { ...initialState.thread },
            };
        },
        [GET_THREAD_COMMENTS_PENDING]: (state, action) => {
            const pending = action.payload === "ASC" ? "nextPending" : "prevPending";

            return { ...state, thread: { ...state.thread, [pending]: true } };
        },
        [GET_THREAD_PREV_COMMENTS_SUCCESS]: (state, action) => {
            const { data } = action.payload;

            const existingResults = state.thread.data?.results ?? [];
            const existingIds = new Set(existingResults?.map((item) => item.id) ?? []);

            const uniqueNewResults = data.results.filter((item) => !existingIds.has(item.id));

            const threadComments = [...existingResults, ...uniqueNewResults];
            return {
                ...state,
                thread: {
                    ...state.thread,
                    data: {
                        results: threadComments,
                        prevToken: data.nextToken,
                    },
                    prevPending: false,
                    nextPending: false,
                },
            };
        },
        [GET_THREAD_NEXT_COMMENTS_SUCCESS]: (state, action) => {
            const { cursor, data } = action.payload;
            const results = state.thread?.data?.results?.filter((el) => el.id !== cursor) ?? [];
            const threadComments = results ? [...data.results.reverse(), ...results] : [...data.results.reverse()];

            return {
                ...state,
                thread: {
                    ...state.thread,
                    data: {
                        results: threadComments,
                        nextToken: data.nextToken,
                    },
                    nextPending: false,
                    prevPending: false,
                },
            };
        },
        [GET_THREAD_COMMENTS_FAILURE]: (state) => {
            return {
                ...state,
                thread: { ...state.thread, prevPending: false },
            };
        },
        [GET_PARENT_COMMENT_SUCCESS]: (state, action) => {
            return {
                ...state,
                comment: {
                    ...state.comment,
                    data: { ...state.comment.data, results: [action.payload] },
                },
                selectedCommentId: action.payload?.id,
            };
        },
        [RESET_THREAD_COMMENTS]: (state) => {
            return { ...state, thread: { ...cloneState.thread } };
        },
        [UPDATE_THREAD_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            const { selectedCommentId } = state;

            if (data.isDeleted) {
                const removedThread = {
                    ...state.thread,
                    data: {
                        ...state.thread.data,
                        results: state.thread.data.results?.map((thread) => (thread.id !== data.id ? thread : data)),
                    },
                };
                const commentData = state.comment.data;
                const modifiedComments = {
                    comment: {
                        ...state.comment,
                        data: {
                            ...commentData,
                            results: commentData.results.map((comment) => {
                                if (comment.id === selectedCommentId) {
                                    return {
                                        ...comment,
                                        subThread: {
                                            ...comment.subThread,
                                            commentCount: comment.subThread.commentCount - 1,
                                        },
                                    };
                                }
                                return comment;
                            }),
                        },
                    },
                };

                return {
                    ...state,
                    ...modifiedComments,
                    thread: removedThread,
                };
            } else {
                const threads = [...state.thread.data.results];
                const threadIndex = threads.findIndex((el) => el.id === data.id);
                threads[threadIndex] = data;

                return {
                    ...state,
                    thread: {
                        ...state.thread,
                        data: { ...state.thread.data, results: threads },
                    },
                };
            }
        },
        [UPDATE_COMMENT_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            const comment = state.comment;

            const isEmptyComment = data.isDeleted && !data.subThread?.commentCount;
            const commentData = state.comment.data;
            if (isEmptyComment) {
                return {
                    ...state,
                    comment: {
                        ...state.comment,
                        data: {
                            ...commentData,
                            results: commentData.results?.map((comment) => (comment.id === data.id ? data : comment)),
                        },
                    },
                    selectedCommentId: data.id === state.selectedCommentId ? null : state.selectedCommentId,
                };
            } else {
                const replies = [...(state.comment.data?.results || [])];
                const replyIndex = replies.findIndex((el) => el.id === data.id);
                replies[replyIndex] = data;

                return {
                    ...state,
                    comment: {
                        ...comment,
                        data: { ...comment.data, results: replies },
                    },
                };
            }
        },
        [ADD_THREAD_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            const newReplies = [...(state.comment?.data?.results || [])];
            const replyIndex = newReplies.findIndex((el) => el.id === state.selectedCommentId);
            const subThread = newReplies.find((el) => el.id === state.selectedCommentId)?.subThread;
            if (subThread)
                newReplies[replyIndex] = {
                    ...newReplies[replyIndex],
                    subThread: {
                        ...subThread,
                        commentCount: subThread.commentCount + 1,
                    },
                };

            const currentResults = state.thread?.data?.results ?? [];
            const filteredThreads = currentResults.filter((item) => item.id !== data.id);
            const threads = [data, ...filteredThreads];

            return {
                ...state,
                comment: {
                    ...state.comment,
                    data: { ...state.comment.data, results: newReplies },
                },
                thread: {
                    ...state.thread,
                    data: { ...state.thread.data, results: threads },
                },
            };
        },
        [ADD_COMMENT_SUCCESS]: (state, action) => {
            const { data } = action.payload;
            const comment = state.comment;
            const currentResults = comment.data?.results ?? [];
            const filteredResults = currentResults.filter((item) => item.id !== data.id);
            const replies = [data, ...filteredResults];

            return {
                ...state,
                comment: {
                    ...state.comment,
                    data: { ...state.comment.data, results: replies },
                },
            };
        },
        [ADD_THREAD_ALL_SUCCESS]: (state, action) => {
            const { data, resourceId, subThreadId } = action.payload;
            const newThreads = [...(state.comment.data?.results || [])];
            const threadIndex = newThreads?.findIndex((el) => el.id === resourceId);
            newThreads[threadIndex].subThread = {
                id: subThreadId,
                commentCount: 1,
            };

            return {
                ...state,
                comment: {
                    ...state.comment,
                    data: { ...state.comment.data, results: newThreads },
                },
                thread: {
                    ...state.thread,
                    data: { ...state.thread.data, results: data?.results },
                    prevPending: null,
                },
            };
        },
        [UPDATE_IS_EDITING_COMMENT_ID]: (state, action) => {
            return { ...state, isEditingCommentId: action.payload };
        },
        [CREATE_THREAD_PENDING]: (state) => {
            return {
                ...state,
                comment: { ...state.comment, createPending: true },
            };
        },
        [CREATE_THREAD_SUCCESS]: (state, action) => {
            const { commentId, threadId, data } = action.payload;
            const comment = state.comment;

            return {
                ...state,
                resourceData: {
                    ...state.resourceData,
                    results: [data, ...(state.resourceData.results ?? [])],
                },
                comment: {
                    ...comment,
                    data: {
                        ...comment.data,
                        results: comment.data?.results?.map((comment) => {
                            if (comment.id === commentId) {
                                return {
                                    ...comment,
                                    subThread: {
                                        id: threadId,
                                        commentCount: 0,
                                    },
                                };
                            }
                            return comment;
                        }),
                    },
                    createPending: false,
                },
            };
        },
        [UPDATE_IS_LINK_COMMENT_USED]: (state, action) => {
            return { ...state, isLinkCommentUsed: action.payload };
        },
        [UPDATE_HAS_LINK_THREAD_LOGIC_EXECUTED]: (state, action) => {
            return { ...state, hasLinkThreadLogicExecuted: action.payload };
        },
        [RESET_STATE]: (state) => {
            Object.values(state.uploadingComments).forEach((threadComments) => {
                threadComments.forEach((comment) => {
                    revokeBlobUrls(comment);
                });
            });

            return {
                ...cloneState,
                uploadingComments: {},
                isLinkCommentUsed: state.isLinkCommentUsed,
                hasLinkThreadLogicExecuted: state.hasLinkThreadLogicExecuted,
            };
        },
        [ADD_UPLOADING_COMMENTS]: (state, action) => {
            const { threadId, data } = action.payload;

            const uploadingCommentThread = state.uploadingComments[threadId];
            if (!uploadingCommentThread) {
                return {
                    ...state,
                    uploadingComments: {
                        ...state.uploadingComments,
                        [threadId]: [data],
                    },
                };
            } else {
                return {
                    ...state,
                    uploadingComments: {
                        ...state.uploadingComments,
                        [threadId]: [...uploadingCommentThread, data],
                    },
                };
            }
        },
        [REMOVE_UPLOADING_COMMENTS]: (state, action) => {
            const { threadId, id } = action.payload;

            const uploadingCommentThread = state.uploadingComments[threadId];
            if (uploadingCommentThread) {
                // 제거되는 댓글의 Blob URL 해제
                const commentToRemove = uploadingCommentThread.find((comment) => comment.id === id);
                if (commentToRemove) {
                    revokeBlobUrls(commentToRemove);
                }

                return {
                    ...state,
                    uploadingComments: {
                        ...state.uploadingComments,
                        [threadId]: uploadingCommentThread.filter((comment) => comment.id !== id),
                    },
                };
            }
            return state;
        },
        [UPDATE_UPLOADING_COMMENTS]: (state, action) => {
            const { threadId, commentId, updatedCommentId } = action.payload;

            const uploadingCommentThread = state.uploadingComments[threadId];
            if (uploadingCommentThread) {
                return {
                    ...state,
                    uploadingComments: {
                        ...state.uploadingComments,
                        [threadId]: uploadingCommentThread.map((comment) =>
                            comment.id === commentId ? { ...comment, id: updatedCommentId } : comment,
                        ),
                    },
                };
            }
        },
    },
    initialState,
);

export const resetState = createAction(RESET_STATE);
export const resetComments = createAction(RESET_COMMENTS);
export const resetThreadComments = createAction(RESET_THREAD_COMMENTS);

export const updateSelectedThreadId = createAction(UPDATE_SELECTED_THREAD_ID);
export const updateSelectedCommentId = createAction(UPDATE_SELECTED_COMMENT_ID);
export const resetSelectedCommentId = createAction(RESET_SELECTED_COMMENT_ID);
export const updateIsEditingCommentId = createAction(UPDATE_IS_EDITING_COMMENT_ID);

export const updateIsLinkCommentUsed = createAction(UPDATE_IS_LINK_COMMENT_USED);
export const updateHasLinkThreadLogicExecuted = createAction(UPDATE_HAS_LINK_THREAD_LOGIC_EXECUTED);
export const addUploadingComments = createAction(ADD_UPLOADING_COMMENTS);
export const removeUploadingComments = createAction(REMOVE_UPLOADING_COMMENTS);
export const updateUploadingComments = createAction(UPDATE_UPLOADING_COMMENTS);

export const getThreads = (projectId, params, signal) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch({ type: GET_RESOURCE_DATA_PENDING });
    const _projectId = projectId || project.id;
    const result = await withAbortHandling(
        getThreadsAPI(stage.endpoint, _projectId, params, signal),
        "스레드 목록 요청이 취소되었습니다",
    );

    if (result.canceled) return result;

    if (result.success) {
        dispatch({
            type: GET_RESOURCE_DATA_SUCCESS,
            payload: { ...result.data.data },
        });
        return { success: true, data: result.data.data };
    } else {
        dispatch({ type: GET_RESOURCE_DATA_FAILURE, payload: result.error });
        return { success: false, error: result.error };
    }
};

export const getThreadMembers = (projectId, threadId, signal) => async (dispatch, getState) => {
    const { stage, project } = getState();
    const _projectId = projectId || project.id;
    const result = await withAbortHandling(
        getThreadMembersAPI(stage.endpoint, _projectId, threadId, signal),
        "스레드 멤버 요청이 취소되었습니다",
    );

    if (result.canceled) return result;

    if (result.success) {
        dispatch({
            type: UPDATE_RESOURCE_DATA_MEMBERS_SUCCESS,
            payload: { id: threadId, members: result.data.data },
        });
        return { success: true, data: result.data.data };
    }

    return result;
};

export const getComments = (projectId, resourceId, params, signal) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch({ type: GET_COMMENTS_PENDING, payload: params.orderType });
    const _projectId = projectId || project.id;
    const actionType =
        params.orderType && params.orderType === "ASC" ? GET_NEXT_COMMENTS_SUCCESS : GET_PREV_COMMENTS_SUCCESS;

    if (!!params.nextToken && !!params.orderType) delete params.orderType;

    const result = await withAbortHandling(
        getCommentsAPI(stage.endpoint, _projectId, resourceId, { ...params, limit: 10 }, signal),
        "댓글 요청이 취소되었습니다",
    );

    if (result.canceled) return result;

    if (result.success) {
        dispatch({
            type: actionType,
            payload: { data: result.data.data, cursor: params.cursor },
        });
        return { success: true, data: result.data.data };
    } else {
        dispatch({ type: GET_COMMENTS_FAILURE, payload: result.error });
        return { success: false, error: result.error };
    }
};

export const getThreadComments = (projectId, resourceId, params, signal) => async (dispatch, getState) => {
    const { stage, comments, project } = getState();
    dispatch({
        type: GET_THREAD_COMMENTS_PENDING,
        payload: params?.orderType,
    });
    const _projectId = projectId || project.id;
    const actionType =
        params?.orderType && params?.orderType === "ASC"
            ? GET_THREAD_NEXT_COMMENTS_SUCCESS
            : GET_THREAD_PREV_COMMENTS_SUCCESS;

    if (!comments.selectedCommentId) {
        const threadResult = await withAbortHandling(
            getThreadAPI(stage.endpoint, _projectId, resourceId, true, signal),
            "스레드 정보 요청이 취소되었습니다",
        );

        if (threadResult.canceled) return threadResult;

        const thread = threadResult.data.data.context;

        const commentResult = await withAbortHandling(
            getCommentAPI(stage.endpoint, _projectId, thread.parentThread.id, thread.resourceId, signal),
            "댓글 정보 요청이 취소되었습니다",
        );

        if (commentResult.canceled) return commentResult;

        await dispatch({
            type: GET_PARENT_COMMENT_SUCCESS,
            payload: commentResult.data.data,
        });
    }

    if (!!params?.nextToken && !!params?.orderType) delete params.orderType;

    const commentsResult = await withAbortHandling(
        getCommentsAPI(stage.endpoint, _projectId, resourceId, { ...params, limit: 10 }, signal),
        "댓글 목록 요청이 취소되었습니다",
    );

    if (commentsResult.canceled) return commentsResult;

    if (commentsResult.success) {
        dispatch({
            type: actionType,
            payload: { data: commentsResult.data.data, cursor: params?.cursor },
        });
        return { success: true, data: commentsResult.data.data };
    } else {
        dispatch({ type: GET_THREAD_COMMENTS_FAILURE, payload: commentsResult.error });
        return { success: false, error: commentsResult.error };
    }
};

export const createThread = (projectId, threadInfo, params, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const { isThread } = threadInfo;

    dispatch({ type: CREATE_THREAD_PENDING });
    return withAbortHandling(
        (async () => {
            const _projectId = projectId || project.id;
            const thread = await createThreadAPI(stage.endpoint, _projectId, params, signal);
            const response = await getThreadAPI(stage.endpoint, _projectId, thread.data.id, undefined, signal);
            let payload = { data: response.data };

            if (isThread) {
                payload = {
                    ...payload,
                    commentId: params.context.resourceId,
                    threadId: thread.data?.id,
                };
                dispatch({ type: CREATE_THREAD_SUCCESS, payload });
            } else {
                dispatch({ type: CREATE_THREAD_SUCCESS, payload });
                dispatch({
                    type: UPDATE_SELECTED_THREAD_ID,
                    payload: thread.data?.id,
                });
            }
            return thread.data?.id;
        })(),
        "스레드 생성 요청이 취소되었습니다",
    );
};

export const createPrivateThread = (projectId, params, signal) => (dispatch, getState) => {
    const { stage, project } = getState();

    return withAbortHandling(
        (async () => {
            const _projectId = projectId || project.id;
            const thread = await createThreadAPI(stage.endpoint, _projectId, params, signal);
            const threadsResult = await dispatch(getThreads(_projectId, params.context, signal));

            if (threadsResult && threadsResult.canceled) {
                throw new Error("Request canceled");
            }

            return thread.data;
        })(),
        "비공개 스레드 생성 요청이 취소되었습니다",
    );
};

export const createComment =
    (projectId, threadInfo, params, signal, dummyCommentId = null) =>
    (dispatch, getState) => {
        const { stage, project } = getState();
        const { isThread, threadId } = threadInfo;

        return withAbortHandling(
            (async () => {
                const _projectId = projectId || project.id;
                const comment = await createCommentAPI(stage.endpoint, _projectId, threadId, params, signal);
                const commentId = comment.data?.id;
                if (dummyCommentId) {
                    dispatch(
                        updateUploadingComments({
                            threadId: threadId,
                            commentId: dummyCommentId,
                            updatedCommentId: commentId,
                        }),
                    );
                }
                const response = await getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal);
                let payload = { data: response.data };

                if (isThread) {
                    dispatch({ type: ADD_THREAD_SUCCESS, payload });
                } else {
                    dispatch({ type: ADD_COMMENT_SUCCESS, payload });
                }
                return comment.data;
            })(),
            "댓글 생성 요청이 취소되었습니다",
        );
    };

export const updateComment = (projectId, threadInfo, commentId, data, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const { isThread, threadId } = threadInfo;

    return withAbortHandling(
        (async () => {
            const _projectId = projectId || project.id;
            await updateCommentAPI(stage.endpoint, _projectId, threadId, commentId, data, signal);
            const response = await getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal);
            let payload = { data: response.data };

            if (isThread) dispatch({ type: UPDATE_THREAD_SUCCESS, payload });
            else dispatch({ type: UPDATE_COMMENT_SUCCESS, payload });
            return true;
        })(),
        "댓글 업데이트 요청이 취소되었습니다",
    );
};

export const deleteComment = (projectId, threadInfo, commentId, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const { isThread, threadId } = threadInfo;

    return withAbortHandling(
        (async () => {
            const _projectId = projectId || project.id;
            await deleteCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal);
            const response = await getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal);
            let payload = { data: response.data };

            if (isThread) dispatch({ type: UPDATE_THREAD_SUCCESS, payload });
            else dispatch({ type: UPDATE_COMMENT_SUCCESS, payload });
            return true;
        })(),
        "댓글 삭제 요청이 취소되었습니다",
    );
};

export const deleteThread = (projectId, threadId, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const _projectId = projectId || project.id;
    return withAbortHandling(
        deleteThreadAPI(stage.endpoint, _projectId, threadId, signal),
        "스레드 삭제 요청이 취소되었습니다",
    );
};

export const postActions = (projectId, threadInfo, commentId, params, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const { isThread, threadId } = threadInfo;

    return withAbortHandling(
        (async () => {
            const _projectId = projectId || project.id;
            await createActionsAPI(stage.endpoint, _projectId, threadId, commentId, params, signal);
            const response = await getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal);
            let payload = { data: response.data };

            if (isThread) dispatch({ type: UPDATE_THREAD_SUCCESS, payload });
            else dispatch({ type: UPDATE_COMMENT_SUCCESS, payload });
            return true;
        })(),
        "액션 생성 요청이 취소되었습니다",
    );
};

export const updateThread = (projectId, threadId, data, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const _projectId = projectId || project.id;
    return withAbortHandling(
        (async () => {
            await updateThreadAPI(stage.endpoint, _projectId, threadId, data, signal);
            const response = await getThreadAPI(stage.endpoint, _projectId, threadId, undefined, signal);
            dispatch({
                type: UPDATE_RESOURCE_DATA_SUCCESS,
                payload: response.data,
            });
            return true;
        })(),
        "스레드 업데이트 요청이 취소되었습니다",
    );
};

export const updateThreadMembers = (projectId, threadId, data, signal) => (dispatch, getState) => {
    const { stage, project } = getState();
    const _projectId = projectId || project.id;
    return withAbortHandling(
        (async () => {
            await updateThreadMembersAPI(stage.endpoint, _projectId, threadId, data, signal);
            const result = await dispatch(getThreadMembers(_projectId, threadId, signal));
            if (result && result.canceled) {
                throw new Error("Request canceled");
            }
            return true;
        })(),
        "스레드 멤버 업데이트 요청이 취소되었습니다",
    );
};

export const deleteCommentAttachments =
    (projectId, threadInfo, commentId, attachmentIds, signal) => async (dispatch, getState) => {
        const { stage, project } = getState();
        const { isThread, threadId } = threadInfo;
        const _projectId = projectId || project.id;
        try {
            await Promise.allSettled(
                attachmentIds.map(async (attachmentId) => {
                    return withAbortHandling(
                        deleteCommentAttachmentAPI(
                            stage.endpoint,
                            _projectId,
                            threadId,
                            commentId,
                            attachmentId,
                            signal,
                        ),
                        `첨부파일(${attachmentId}) 삭제 요청이 취소되었습니다`,
                    );
                }),
            );

            const result = await withAbortHandling(
                getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal),
                "댓글 정보 요청이 취소되었습니다",
            );

            if (!result.canceled) {
                const payload = { data: result.data.data };
                if (isThread) dispatch({ type: UPDATE_THREAD_SUCCESS, payload });
                else dispatch({ type: UPDATE_COMMENT_SUCCESS, payload });
            }
        } catch (e) {
            console.error("첨부파일 삭제 중 오류 발생:", e);
        }
    };

export const addCommentAttachments = (projectId, threadInfo, commentId, data, signal) => async (dispatch, getState) => {
    const { stage, project } = getState();
    const { isThread, threadId } = threadInfo;
    const _projectId = projectId || project.id;

    const addResult = await withAbortHandling(
        addCommentAttachmentsAPI(stage.endpoint, _projectId, threadId, commentId, data, signal),
        "첨부파일 추가 요청이 취소되었습니다",
    );

    if (addResult.canceled) return;

    const commentResult = await withAbortHandling(
        getCommentAPI(stage.endpoint, _projectId, threadId, commentId, signal),
        "댓글 정보 요청이 취소되었습니다",
    );

    if (commentResult.canceled) return;

    const payload = { data: commentResult.data.data };
    if (isThread) dispatch({ type: UPDATE_THREAD_SUCCESS, payload });
    else dispatch({ type: UPDATE_COMMENT_SUCCESS, payload });
};

export const resetCommentLinkState = () => (dispatch) => {
    dispatch(updateIsLinkCommentUsed(false));
    dispatch(updateHasLinkThreadLogicExecuted(false));
    dispatch(updateSelectedCommentId(null));
};
